import { Store } from "redux";

import { EntityType } from "@mapmycustomers/shared";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import { FILTER_ENTITY_TYPES } from "@app/component/filtering/utils/consts";
import type { TourStepAction } from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import i18nService from "@app/config/I18nService";
import { selectMapTool } from "@app/scene/map/store/actions";
import { fetchPins } from "@app/scene/map/store/mapMode/actions";
import { getMapViewState } from "@app/scene/map/store/mapMode/selectors";
import type { Actions, RootState } from "@app/store/rootReducer";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";

import { errorMessages } from "./messages";

export const T_GROUP_FIELD_NAME = "T Group";
export const DEFAULT_T_GROUP_VALUE = 200;

export const BULK_EDIT_VALUE_INPUT_SELECTOR =
  "[class*=BulkEdit_popoverContainer] [class*=FieldEditor_container] .ant-input-number-input";
// the visible bordered box of the numeric editor (input + spinner), without the field label
export const BULK_EDIT_VALUE_BOX_SELECTOR =
  "[class*=BulkEdit_popoverContainer] [class*=FieldEditor_container] .ant-input-number";

type TourStore = Store<RootState, Actions>;

export const getTGroupField = (): IField | undefined =>
  companyFieldModel.fields.find((field) => field.displayName === T_GROUP_FIELD_NAME);

// Mirrors ToolSelectionPanel's own toggle-off path (handleToolClick dispatches
// selectMapTool.request(undefined) to deselect the active tool). The saga treats a missing
// mapTool the same as any other selection — no LASSO-specific branch runs — so this is a
// safe no-op when no tool is active. Needed because the tour can end in lasso mode (see the
// `apply` step's onAfter), and a leftover tool would hide the filter bar on the next run.
export const deselectMapTool = (store: TourStore) => {
  store.dispatch(selectMapTool.request(undefined));
};

// The fetchPins saga (not bare applyMapViewSettings) is the UI "Clear All" path: it applies
// the view settings, refetches pins, and persists mapViewSettings2 metadata. Every entity
// key must be explicitly undefined — the reducer shallow-merges, so `filter: {}` is a no-op.
// Also narrows visible entities to companies, mirroring the popover's own
// applyFilterChanges -> onVisibleEntitiesChange path that runs when a company filter is typed
// manually. Without it, person/deal pins stay visible, a mixed-entity lasso selection disables
// the Bulk Edit button, and step 9 never fires.
export const prepareMapForTour = (store: TourStore) => {
  store.dispatch(selectSavedFilter({ entityType: EntityType.PIN, savedFilter: undefined }));
  store.dispatch(
    fetchPins.request({
      request: {
        filter: {
          [EntityType.COMPANY]: undefined,
          [EntityType.DEAL]: undefined,
          [EntityType.PERSON]: undefined,
          universal: undefined,
        },
        selectedSavedFilterId: undefined,
        visibleEntities: [EntityType.COMPANY],
      },
    })
  );
};

export const isMapUnpreparedForTour = (store: TourStore): boolean => {
  const { filter, visibleEntities } = getMapViewState(store.getState());
  return (
    FILTER_ENTITY_TYPES.some((entityType) => !!filter?.[entityType]?.conditions.length) ||
    visibleEntities.length !== 1
  );
};

// The Map scene re-applies persisted view settings over the store on mount
// (onInitializeMapView), and the persisted metadata only updates after an API round-trip.
// Dispatching view-state changes before the map has mounted and settled would get them
// overwritten, so wait for the filter bar to render plus a settle delay.
export const waitForMapReady = () =>
  new Promise<void>((resolve) => {
    waitForElement("#map-filter-button", () => window.setTimeout(resolve, 500), { timeout: 0 });
  });

export const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

export const initializeJasperBulkEditTourOnBefore = async ({
  store,
}: {
  store: TourStore;
}): Promise<TourStepAction | void> => {
  if (!getTGroupField()) {
    return {
      action: "error" as const,
      message: i18nService.formatMessage(errorMessages.noTGroupField),
    };
  }
  // A re-run can start with the tour's own leftover lasso mode active (see the `apply` step's
  // onAfter) — TopPanel hides #map-filter-button while a tool is selected, so this must run
  // before waitForMapReady, or its uncapped wait (timeout: 0) hangs forever.
  deselectMapTool(store);
  await waitForMapReady();
  prepareMapForTour(store);
  // map init may still overwrite the prepared state with persisted filters or the full
  // visible-entities set — verify both, retry once
  await delay(1000);
  if (isMapUnpreparedForTour(store)) {
    prepareMapForTour(store);
  }
};

export const openCompanyFilterTypeMenuOnBefore = async (
  selectFilterTypeButton: string,
  companyTypeMenuItem: string
) => {
  // open the "Select filter type" menu for the user
  await new Promise<void>((resolve) => {
    waitForElement(selectFilterTypeButton, () => {
      document.querySelector<HTMLElement>(selectFilterTypeButton)?.click();
      resolve();
    });
  });
  await new Promise<void>((resolve) => {
    waitForElement(companyTypeMenuItem, resolve);
  });
};
